/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { LAppDelegate } from './lappdelegate';
import { DynamicModelLoader } from './dynamic_model_loader';
import { Live2DModelAPI, setReady } from './api/live2dmodel-api';

// 声明全局配置接口
interface Window {
  Live2DConfig?: {
    modelName?: string;
    texturePath?: string;
    showBackground?: boolean;
    backgroundImage?: string;
    shaderPath?: string;
  };
}

/**
 * 初始化时序策略
 * ----------------------------------------------------------------------------
 * live2dplugin.js 以 <script type="module"> 引入，按 defer 语义在 DOM 解析
 * 完成后执行。此时宿主页（如 Vue）的容器元素可能尚未渲染（异步组件 / 路由 /
 * 登录后刷新 / WASM 与大量资源加载等），存在时序竞争：
 *   - querySelector('[data-cubism-model]') 未命中 → canvas 被追加到 document.body
 *     导致模型不可见
 *   - initApp() 仅执行一次，失败后无重试
 *
 * 处理流程：
 *   1. 立即尝试初始化（不等 load 事件）
 *   2. 容器 / 配置未就绪 → 5s 后重试一次（重试期间不产生任何副作用）
 *   3. 再次未就绪 → 静默放弃定时重试
 *   4. MutationObserver 持续监听容器出现，覆盖极端延迟场景（DOM 通知渲染）
 */

/** 失败后重试间隔（ms） */
const RETRY_DELAY_MS = 5000;
/** 最大尝试次数（首次 + 1 次重试） */
const MAX_ATTEMPTS = 2;
/** 容器选择器（任一匹配即视为容器就绪） */
const CONTAINER_SELECTOR = '#live2d-container, [data-cubism-model]';
/** URL 参数白名单（存在任一即视为显式配置，不进入重试流程） */
const URL_CONFIG_KEYS = [
  'modelName',
  'texturePath',
  'showBackground',
  'backgroundImage',
  'shaderPath'
] as const;

/** 初始化尝试次数（含首次） */
let initAttempt = 0;
/** 重试定时器句柄 */
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** DOM 观察器（兜底） */
let domObserver: MutationObserver | null = null;
/** 是否已进入真正初始化流程（防重入） */
let initialized = false;

/**
 * 查找 Live2D 容器元素
 */
function findContainer(): HTMLElement | null {
  return document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
}

/**
 * 是否存在显式配置（URL 参数或全局容器引用）
 * 用于区分「容器未就绪（时序竞争）」与「显式无容器配置（URL 驱动 / _live2dContainer）」。
 * 存在显式配置时不进入重试流程，按原 fallback 行为初始化。
 */
function hasExplicitConfig(): boolean {
  if ((window as any)._live2dContainer) return true;
  const params = new URLSearchParams(window.location.search);
  return URL_CONFIG_KEYS.some(k => params.has(k));
}

/**
 * 安排一次重试：仅重试到 MAX_ATTEMPTS 次，再次失败则静默（依赖 DOM 观察兜底）。
 */
function scheduleRetry(): void {
  if (retryTimer) return;
  if (initAttempt >= MAX_ATTEMPTS) {
    console.warn(
      `[Live2D] 第 ${initAttempt} 次尝试仍未就绪，进入静默模式，等待 DOM 通知渲染`
    );
    return;
  }
  console.warn(`[Live2D] ${RETRY_DELAY_MS}ms 后重试...`);
  retryTimer = setTimeout(() => {
    retryTimer = null;
    void initApp();
  }, RETRY_DELAY_MS);
}

/**
 * 启动 DOM 观察器：监听容器元素出现（极端情况兜底，如 Vue 异步渲染超 5s）。
 * 一旦检测到容器立即触发初始化，不受定时重试次数限制。
 */
function startDomObserver(): void {
  if (domObserver || typeof MutationObserver === 'undefined') return;
  domObserver = new MutationObserver(() => {
    if (initialized) return;
    if (findContainer()) {
      console.log('[Live2D] DOM 观察器检测到容器出现，立即触发初始化');
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      void initApp();
    }
  });
  domObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
}

/**
 * 停止重试定时器与 DOM 观察器
 */
function stopRetryAndObserve(): void {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  if (domObserver) {
    domObserver.disconnect();
    domObserver = null;
  }
}

/**
 * アプリケーションの初期化処理
 */
async function initApp(): Promise<void> {
  if (initialized) return;
  initAttempt++;
  console.log(`[Live2D] initApp() 第 ${initAttempt} 次尝试初始化...`);

  const configElement = findContainer();

  // 容器未就绪且无显式配置 → 视为时序竞争，进入重试流程
  // 注意：此处不调用 LAppDelegate.initialize()，避免 canvas 被误挂到 document.body，
  // 同时保证重试的幂等性（单例未产生任何副作用）。
  if (!configElement && !hasExplicitConfig()) {
    console.warn(
      `[Live2D] 未找到容器 "${CONTAINER_SELECTOR}"，也无可用的 URL / 全局配置`
    );
    scheduleRetry();
    return;
  }

  // 容器 / 配置就绪，标记并清理重试与观察
  initialized = true;
  stopRetryAndObserve();

  let modelName = 'Haru';
  let texturePath: string | undefined;
  let showBackground = true;
  let backgroundImage = '';
  let shaderPath = '';
  let targetContainer: HTMLElement | undefined = undefined;

  if (configElement) {
    const htmlModelName = configElement.getAttribute('data-cubism-model');
    const htmlTexturePath = configElement.getAttribute('data-model-path');
    const htmlShowBg = configElement.getAttribute('data-show-background');
    const htmlBgImage = configElement.getAttribute('data-background-image');
    const htmlShaderPath = configElement.getAttribute('data-shader-path');
    const htmlContainer = configElement.getAttribute('data-container');

    if (htmlModelName) modelName = htmlModelName;
    if (htmlTexturePath) texturePath = htmlTexturePath;
    if (htmlShowBg !== null) showBackground = htmlShowBg === 'true';
    if (htmlBgImage !== null) backgroundImage = htmlBgImage;
    if (htmlShaderPath !== null) shaderPath = htmlShaderPath;

    if (htmlContainer) {
      targetContainer = document.querySelector(htmlContainer) as HTMLElement || undefined;
      if (!targetContainer) {
        console.warn(`[Live2D] data-container "${htmlContainer}" 未匹配到任何元素`);
      }
    }
    if (!targetContainer) targetContainer = configElement as HTMLElement;
    if ((window as any)._live2dContainer) targetContainer = (window as any)._live2dContainer;

    console.log(`[Live2D] 配置: model=${modelName}, texture=${texturePath || '(模型目录)'}, bg=${showBackground}, shader=${shaderPath || '默认'}`);
  } else {
    console.log('[Live2D] 未检测到 HTML 容器属性，使用 URL / 全局配置');
    const urlParams = new URLSearchParams(window.location.search);
    const urlModelName = urlParams.get('modelName');
    const urlTexturePath = urlParams.get('texturePath');
    const urlShowBg = urlParams.get('showBackground');
    const urlBgImage = urlParams.get('backgroundImage');
    const urlShaderPath = urlParams.get('shaderPath');

    if (urlModelName) modelName = urlModelName;
    if (urlTexturePath) texturePath = urlTexturePath;
    if (urlShowBg !== null) showBackground = urlShowBg === 'true';
    if (urlBgImage !== null) backgroundImage = urlBgImage;
    if (urlShaderPath !== null) shaderPath = urlShaderPath;

    if ((window as any)._live2dContainer) targetContainer = (window as any)._live2dContainer;
  }

  (window as any).Live2DConfig = {
    modelName,
    texturePath,
    showBackground,
    backgroundImage,
    shaderPath
  };

  console.log('[Live2D] 开始初始化 WebGL...');

  if (!LAppDelegate.getInstance().initialize(targetContainer)) {
    console.error('[Live2D] LAppDelegate.initialize() 失败!');
    return;
  }

  try {
    console.log('[Live2D] 等待模型加载...');
    const model = await DynamicModelLoader.loadModelFromHtmlConfig();

    if (model) {
      console.log('[Live2D] 模型就绪，等待着色器...');
      await model.whenShadersReady();
    }

    setReady();
    LAppDelegate.getInstance().run();
  } catch (e) {
    console.error('[Live2D] 初始化失败:', e);
    LAppDelegate.getInstance().run();
  }
}

// module script 执行时 DOM 已解析完成，立即尝试初始化（不等 load 事件，避免时序竞争）
void initApp();
// 启动 DOM 观察兜底（应对 Vue 异步渲染等极端情况，由 DOM 通知渲染）
startDomObserver();

window.addEventListener(
  'beforeunload',
  (): void => LAppDelegate.releaseInstance(),
  { passive: true }
);

(window as any).Live2DModel = Live2DModelAPI;
